import { Injectable, Inject } from "@angular/core";
import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { OverlaySpinnerComponent } from "./overlaySpinner.component";
import { Observable, Subject } from "rxjs";
import { RouterHelperService } from "../../services/routerHelper.service";

interface OverlaySpinerConfig {
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
}

const DEFAULT_CONFIG: OverlaySpinerConfig = {
  hasBackdrop: true,
  backdropClass: "overlay-light-backdrop",
  panelClass: "overlay-hide-spinner-panel",
};

const OVERLAY_HIDDEN_TIME = 300;
const MINIMUM_VISIBLE_TIME = 2000;

@Injectable()
export class OverlaySpinnerService {
  private overlayRef: OverlayRef = null;
  private overlayCallerCounter = 0;
  private darkLayerStart: Date = null;
  private _initialLoadSubject: Subject<void> = new Subject<void>();
  constructor(private overlay: Overlay
    , private _routerHelperService: RouterHelperService) { }

  public get InitialLoadObserver(): Observable<void> {
    return this._initialLoadSubject.asObservable();
  }

  /**
   * show spinner layout on page
   * @param config get config for spinner
   * @result return a Portal that can be used for closing spinner.
   */
  public ShowLayoutSpinner(config: OverlaySpinerConfig = {}): void {
    const me = this;

    if (!me.overlayRef) {
      // Returns an OverlayRef which is a PortalHost
      me.overlayRef = this.createOverlay(config);
    }

    const containerPortal = new ComponentPortal(OverlaySpinnerComponent);
    // show spinner in the first call by component
    me.overlayCallerCounter += 1;
    if (!me.overlayRef.hasAttached() && me.overlayCallerCounter === 1) {
      me.overlayRef.attach(containerPortal);
      const backDropElements = document.getElementsByClassName("cdk-overlay-backdrop");
      // the last overlay is spinner(e.g maybe a modal dialog is open when want to show spinner)
      const backDropElement = backDropElements[backDropElements.length - 1];
      const overlayPaneElement = document.getElementsByClassName("overlay-hide-spinner-panel")[0];
      backDropElement.classList.add("overlay-light-backdrop");
      setTimeout(() => {
        if (!!me.overlayRef && me.overlayRef.hasAttached()) {
          me.darkLayerStart = new Date();
          backDropElement.classList.remove("overlay-light-backdrop");
          backDropElement.classList.add("overlay-dark-backdrop");
          overlayPaneElement.classList.add("overlay-show-spinner-panel");
        }
      }, OVERLAY_HIDDEN_TIME);
    }

    // overlayRef.backdropClick().subscribe(_ => overlayRef.close());
  }

  /**
   * hide spinner
   */
  public HideLayoutSpinner(): void {
    const me = this;
    const startHide = new Date();
    if (me.overlayCallerCounter > 0) {
      const overlayPaneElement = document.getElementsByClassName("overlay-hide-spinner-panel")[0];
      me.overlayCallerCounter -= 1;
      if (!!me.overlayRef && me.overlayRef.hasAttached() && me.overlayCallerCounter === 0) {
        if ((me.darkLayerStart == null) || (startHide.getTime() - me.darkLayerStart.getTime() >= MINIMUM_VISIBLE_TIME)) {
          // we should remove class for spinner before detach the portal for the next call.
          overlayPaneElement.classList.remove("overlay-show-spinner-panel");
          me.overlayRef.detach();
          me._initialLoadSubject.next();
          me._routerHelperService.CHeckMainLayerHeight();
        } else {
          setTimeout(() => {
            if (!!me.overlayRef && me.overlayRef.hasAttached()) {
              // we should remove class for spinner before detach the portal for the next call.
              overlayPaneElement.classList.remove("overlay-show-spinner-panel");
              me.overlayRef.detach();
              me._initialLoadSubject.next();
              me._routerHelperService.CHeckMainLayerHeight();
            }
          }, MINIMUM_VISIBLE_TIME - (startHide.getTime() - me.darkLayerStart.getTime()));
        }
      } else {
        // Do not detach portal
      }
    }
  }

  private createOverlay(config: OverlaySpinerConfig): OverlayRef {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };
    const overlayConfig = this.getOverlayConfig(dialogConfig);
    return this.overlay.create(overlayConfig);
  }

  private getOverlayConfig(config: OverlaySpinerConfig): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy
    });

    return overlayConfig;
  }

}
